import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BarChart2,
  Dribbble,
  Save,
  Check,
  X,
  CalendarDays,
  Loader2,
} from "lucide-react";
import i18n from "../i18n";
import { BackButton } from "../components/BackButton";


/* ===== i18n mini-hook ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = useCallback(
    (key: string, options?: Record<string, unknown>) =>
      String((i18n.t as any)(key, options)),
    []
  );
  return { t, lng };
}

type Player = {
  firstName: string;
  lastName: string;
  number: number;
  position: string;
};

type Match = {
  id: string;
  opponent: string;
  date: string;
  players?: Player[] | null | string;
};

type PlayerStats = {
  player: Player;
  points: number;
  assists: number;
  rebounds: number;
  steals: number;
  blocks: number;
  turnovers: number;
  shots_attempted: number;
  shots_made: number;
  ft_attempted: number;
  ft_made: number;
  three_attempted: number;
  three_made: number;
};

// liste des clés de stats
const statKeyList: (keyof Omit<PlayerStats, "player">)[] = [
  "points",
  "assists",
  "rebounds",
  "steals",
  "blocks",
  "turnovers",
  "shots_attempted",
  "shots_made",
  "ft_attempted",
  "ft_made",
  "three_attempted",
  "three_made",
];

export const MatchStats = () => {
  const { t, lng } = useI18nSafe();

  const [matches, setMatches] = useState<Match[]>([]);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [stats, setStats] = useState<PlayerStats[]>([]);
  const [selectedShooter, setSelectedShooter] = useState<string>("");
  const [shots, setShots] = useState<{ x: number; y: number; made: boolean }[]>([]);
  const [shotResult, setShotResult] = useState<"made" | "missed" | null>(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const editShots = queryParams.get("editShots") === "true";
  const matchIdFromUrl = queryParams.get("matchId");

  const courtRef = useRef<HTMLImageElement>(null);
  const [activeTab, setActiveTab] = useState<"stats" | "shots">(editShots ? "shots" : "stats");

  const locale = lng === "fr" ? "fr-FR" : "en-US";

  // ————— Helpers —————
  const normalizePlayers = useCallback((players: Match["players"]) => {
    if (!players) return [];
    if (typeof players === "string") {
      try {
        const parsed = JSON.parse(players);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return Array.isArray(players) ? players : [];
  }, []);

  const primeStatsForMatch = useCallback(
    (match: Match | null) => {
      if (!match) {
        setStats([]);
        return;
      }
      const roster = normalizePlayers(match.players);
      if (roster.length === 0) {
        alert(t("matchstats:alerts.noPlayers"));
        navigate("/allmatchs");
        return;
      }
      const initial: PlayerStats[] = roster.map((player) => ({
        player,
        points: 0,
        assists: 0,
        rebounds: 0,
        steals: 0,
        blocks: 0,
        turnovers: 0,
        shots_attempted: 0,
        shots_made: 0,
        ft_attempted: 0,
        ft_made: 0,
        three_attempted: 0,
        three_made: 0,
      }));
      setStats(initial);
    },
    [normalizePlayers, navigate, t]
  );

  // ————— Fetch matches —————
  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/login");
        return;
      }
      const { data, error } = await supabase
        .from("matches")
        .select("*")
        .eq("user_id", user.id)
        .order("date", { ascending: false });

      if (error) {
        console.error("Erreur récupération des matchs :", error);
        setMatches([]);
      } else {
        setMatches(data || []);
      }
      setLoading(false);
    })();
  }, [navigate]);

  // ————— Sélection / auto-sélection —————
  const handleMatchSelect = useCallback(
    (matchId: string) => {
      const match = matches.find((m) => m.id === matchId) || null;
      setSelectedMatch(match);
      primeStatsForMatch(match);
      setSelectedShooter("");
      setShotResult(null);
    },
    [matches, primeStatsForMatch]
  );

  useEffect(() => {
    if (matchIdFromUrl && matches.length > 0) {
      const auto = matches.find((m) => m.id === matchIdFromUrl);
      if (auto) handleMatchSelect(auto.id);
    }
  }, [matchIdFromUrl, matches, handleMatchSelect]);

  // ————— Charger les tirs existants à chaque changement de match —————
  useEffect(() => {
    (async () => {
      if (!selectedMatch) {
        setShots([]);
        return;
      }
      const { data, error } = await supabase
        .from("shot_locations")
        .select("x,y,made")
        .eq("match_id", selectedMatch.id);

      if (error) {
        console.error("Erreur chargement tirs :", error);
        setShots([]);
      } else {
        setShots((data || []).map((s) => ({ x: s.x, y: s.y, made: !!s.made })));
      }
    })();
  }, [selectedMatch]);

  // ————— Handlers —————
  const handleStatChange = (
    index: number,
    field: keyof Omit<PlayerStats, "player">,
    value: number
  ) => {
    const updated = [...stats];
    updated[index][field] = value;
    setStats(updated);
  };

  const handleSubmit = async () => {
    if (!selectedMatch) return;

    const { data: existingStats, error: fetchError } = await supabase
      .from("match_stats")
      .select("id")
      .eq("match_id", selectedMatch.id);

    if (fetchError) {
      console.error("Erreur lors de la vérification :", fetchError);
      alert(t("matchstats:alerts.genericError"));
      return;
    }

    if (existingStats && existingStats.length > 0) {
      alert(t("matchstats:alerts.alreadySaved"));
      return;
    }

    const insertData = stats.map((s) => ({
      match_id: selectedMatch.id,
      player_number: s.player.number,
      player_name: `${s.player.firstName} ${s.player.lastName}`,
      position: s.player.position,
      points: s.points,
      assists: s.assists,
      rebounds: s.rebounds,
      steals: s.steals,
      blocks: s.blocks,
      turnovers: s.turnovers,
      shots_attempted: s.shots_attempted,
      shots_made: s.shots_made,
      ft_attempted: s.ft_attempted,
      ft_made: s.ft_made,
      three_attempted: s.three_attempted,
      three_made: s.three_made,
    }));

    const { error: insertError } = await supabase.from("match_stats").insert(insertData);

    if (insertError) {
      console.error("Erreur insertion des stats :", insertError);
      alert(t("matchstats:alerts.saveError"));
    } else {
      alert(t("matchstats:alerts.saveOk"));
      navigate("/allmatchs");
    }
  };

  const handleCourtClick = async (e: React.MouseEvent<HTMLImageElement>) => {
    if (!selectedMatch || !selectedShooter || shotResult === null) return;

    const rect = courtRef.current?.getBoundingClientRect();
    if (!rect) return;
    const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
    const yPercent = ((e.clientY - rect.top) / rect.height) * 100;

    const { error } = await supabase.from("shot_locations").insert({
      match_id: selectedMatch.id,
      x: xPercent,
      y: yPercent,
      player_number: Number(selectedShooter),
      made: shotResult === "made",
    });

    if (error) {
      console.error("Erreur ajout tir :", error);
      alert(t("matchstats:alerts.shotSaveError"));
    } else {
      setShots((prev) => [...prev, { x: xPercent, y: yPercent, made: shotResult === "made" }]);
    }
  };

  return (
    <section className="p-6 max-w-4xl mx-auto min-h-screen bg-gray-50 dark:bg-gray-950 dark:text-gray-100">
      {/* Header app sticky */}
      <div className="sticky top-0 z-50 -mx-6 mb-6 bg-gray-50/95 dark:bg-gray-950/95 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="relative px-6 py-3 pt-[calc(env(safe-area-inset-top,0px)+0.75rem)]">
          {/* Back */}
          <div className="absolute left-6 top-1/2 -translate-y-1/2">
            <BackButton fallback="/allmatchs" />
          </div>

          {/* Title centered */}
          <h1 className="text-lg font-bold text-orange-500 dark:text-orange-400 text-center">
            {t("matchstats:title")}
          </h1>
        </div>
      </div>

      {/* Sélecteur de match */}
      <div className="mb-6">
        <label className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <CalendarDays className="w-4 h-4" />
          {t("matchstats:selectors.match.label")}
        </label>
        <select
          className="w-full p-2 rounded border bg-white text-gray-900 border-gray-300
                     dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
          value={selectedMatch?.id ?? ""}
          onChange={(e) => handleMatchSelect(e.target.value)}
        >
          <option value="" disabled>
            — {t("matchstats:selectors.match.placeholder")} —
          </option>
          {matches.map((m) => (
            <option key={m.id} value={m.id}>
              {new Date(m.date).toLocaleDateString(locale)} — {t("matchstats:selectors.match.vs")} {m.opponent}
            </option>
          ))}
        </select>
      </div>

      {/* Onglets */}
      {selectedMatch && (
        <div className="mb-6 flex flex-wrap gap-3 justify-center">
          <button
            onClick={() => setActiveTab("stats")}
            className={`px-4 py-2 rounded inline-flex items-center gap-2 ${activeTab === "stats"
                ? "bg-orange-500 text-white"
                : "bg-gray-200 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
              }`}
          >
            <BarChart2 className="w-4 h-4" />
            {t("matchstats:tabs.stats")}
          </button>
          <button
            onClick={() => setActiveTab("shots")}
            className={`px-4 py-2 rounded inline-flex items-center gap-2 ${activeTab === "shots"
                ? "bg-orange-500 text-white"
                : "bg-gray-200 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
              }`}
          >
            <Dribbble className="w-4 h-4" />
            {t("matchstats:tabs.shots")}
          </button>
        </div>
      )}

      {/* Onglet Stats */}
      {activeTab === "stats" && selectedMatch && stats.length > 0 && (
        <div className="space-y-6">
          {stats.map((s, index) => (
            <div
              key={index}
              className="p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded shadow"
            >
              <h2 className="text-lg font-bold text-gray-700 dark:text-gray-200 mb-2">
                {s.player.number} - {s.player.firstName} {s.player.lastName}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {statKeyList.map((stat) => (
                  <label key={stat} className="flex flex-col gap-1">
                    <span className="text-gray-700 dark:text-gray-300">
                      {t(`matchstats:statLabels.${stat}`)}
                    </span>
                    <input
                      type="number"
                      min={0}
                      value={s[stat]}
                      onChange={(e) =>
                        handleStatChange(
                          index,
                          stat,
                          Number.isNaN(parseInt(e.target.value))
                            ? 0
                            : parseInt(e.target.value)
                        )
                      }
                      className="border rounded px-2 py-1 bg-white text-gray-900 border-gray-300
                                 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                    />
                  </label>
                ))}
              </div>
            </div>
          ))}

          <button
            onClick={handleSubmit}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded inline-flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {t("matchstats:buttons.saveStats")}
          </button>
        </div>
      )}

      {/* Onglet Tirs */}
      {activeTab === "shots" && selectedMatch && (
        <div>
          <div className="mb-4">
            <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("matchstats:selectors.player.label")}
            </label>
            <select
              className="w-full p-2 rounded border bg-white text-gray-900 border-gray-300
                         dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
              value={selectedShooter}
              onChange={(e) => setSelectedShooter(e.target.value)}
            >
              <option value="">{t("matchstats:selectors.player.placeholder")}</option>
              {normalizePlayers(selectedMatch.players).map((p) => (
                <option key={p.number} value={p.number}>
                  {p.number} - {p.firstName} {p.lastName}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4 flex gap-3">
            <button
              className={`px-4 py-2 rounded inline-flex items-center gap-2 ${shotResult === "made"
                  ? "bg-green-600 text-white"
                  : "bg-gray-200 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                }`}
              onClick={() => setShotResult("made")}
            >
              <Check className="w-4 h-4" />
              {t("matchstats:buttons.shotMade")}
            </button>
            <button
              className={`px-4 py-2 rounded inline-flex items-center gap-2 ${shotResult === "missed"
                  ? "bg-red-600 text-white"
                  : "bg-gray-200 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                }`}
              onClick={() => setShotResult("missed")}
            >
              <X className="w-4 h-4" />
              {t("matchstats:buttons.shotMissed")}
            </button>
          </div>

          <div className="relative w-full max-w-3xl mx-auto">
            <img
              ref={courtRef}
              src="/court.png"
              alt={t("matchstats:aria.courtAlt")}
              onClick={handleCourtClick}
              className="w-full h-auto select-none border border-gray-200 dark:border-gray-800 rounded"
            />
            {shots.map((shot, index) => (
              <div
                key={index}
                className={`absolute w-4 h-4 rounded-full ${shot.made ? "bg-green-500" : "bg-red-500"
                  }`}
                style={{
                  top: `${shot.y}%`,
                  left: `${shot.x}%`,
                  transform: "translate(-50%, -50%)",
                  pointerEvents: "none",
                }}
              />
            ))}
          </div>
        </div>
      )}

      {loading && (
        <p className="mt-8 text-center text-gray-600 dark:text-gray-300 inline-flex items-center gap-2 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t("matchstats:loading")}
        </p>
      )}
    </section>
  );
};

export default MatchStats;
